// Authored by Puneet Kaura

import router from 'umi/router';
import { productFruits, ProductFruitsUserObject } from 'product-fruits';

const PRODUCT_FRUITS_WORKSPACE_CODE = 'TwaJ89tbQnNfqcHO';
const PRODUCT_FRUITS_LANGUAGE = 'en';

export type ProductFruitsBridgeUser = {
  email?: string;
  id?: number | string;
  user_type?: number | string;
  geo?: string;
  designation?: string;
  plan?: number | string;
};

let hasInitialized = false;
let lastUserSignature: string | null = null;
let syncIntervalId: number | null = null;

const isBrowser = () => typeof window !== 'undefined';
const PRODUCT_FRUITS_SYNC_INTERVAL_MS = 1000;

const normalizeAnalyticsValue = (value?: string | number | null): string | number => {
  if (value === null || value === undefined) return 'none';
  if (typeof value === 'string' && value.trim() === '') return 'none';
  return value;
};

const getCurrentLocation = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const navigateWithinApp = (location: string) => {
  if (!location) return;

  if (/^https?:\/\//i.test(location)) {
    if (location.startsWith(window.location.origin)) {
      router.push(location.slice(window.location.origin.length) || '/');
      return;
    }

    window.location.assign(location);
    return;
  }

  router.push(location.startsWith('/') ? location : `/${location}`);
};

const getInitOptions = () => ({
  customNavigation: {
    use: true,
    onGet: getCurrentLocation,
    navigate: navigateWithinApp,
  },
});

const identifyProductFruitsUser = (userInfo: ProductFruitsUserObject) => {
  const performIdentify = () => {
    if (window.productFruits?.identifyUser) {
      window.productFruits.identifyUser(userInfo);
    }
  };

  if (window.productFruitsIsReady) {
    performIdentify();
    return;
  }

  productFruits.safeExec(() => {
    performIdentify();
  });
};

export const buildProductFruitsUser = (
  user?: ProductFruitsBridgeUser | null,
): ProductFruitsUserObject | null => {
  if (!user?.email) return null;

  const [, emailDomain] = user.email.split('@');
  const props: ProductFruitsUserObject['props'] = {
    user_django_db_id: user.id ?? '',
    user_type: user.user_type ?? '',
    geo: normalizeAnalyticsValue(user.geo),
    designation: normalizeAnalyticsValue(user.designation),
    plan: normalizeAnalyticsValue(user.plan),
  };

  if (emailDomain) {
    props.email_domain = emailDomain;
  }

  return {
    username: user.email,
    email: user.email,
    props,
  };
};

export const initOrUpdateProductFruits = (userInfo: ProductFruitsUserObject) => {
  const userSignature = JSON.stringify(userInfo);
  if (lastUserSignature === userSignature) return;

  if (!hasInitialized) {
    productFruits.init(
      PRODUCT_FRUITS_WORKSPACE_CODE,
      PRODUCT_FRUITS_LANGUAGE,
      userInfo,
      getInitOptions(),
    );
    hasInitialized = true;
    lastUserSignature = userSignature;
    return;
  }

  identifyProductFruitsUser(userInfo);
  lastUserSignature = userSignature;
};

export const destroyProductFruits = () => {
  lastUserSignature = null;
  hasInitialized = false;

  if (!isBrowser()) return;

  try {
    const productFruitsServices = (window.productFruits as any)?.services;
    if (productFruitsServices?.destroy) {
      productFruitsServices.destroy();
    }
  } catch (error) {
    console.error('Product Fruits destroy failed', error);
  }
};

const normalizeStoredValue = (value: string | null): string | number | undefined => {
  if (value === null || value === '') return undefined;

  const numericValue = Number(value);
  if (!Number.isNaN(numericValue) && value.trim() !== '') {
    return numericValue;
  }

  return value;
};

const readStoredProductFruitsUser = (): ProductFruitsBridgeUser | null => {
  if (!isBrowser()) return null;

  const email = window.localStorage.getItem('User') || undefined;
  if (!email) return null;

  return {
    email,
    id: normalizeStoredValue(window.localStorage.getItem('UserId')),
    user_type: normalizeStoredValue(window.localStorage.getItem('UserType')),
    geo: window.localStorage.getItem('UserGeo') || undefined,
    designation: window.localStorage.getItem('UserDesignation') || undefined,
    plan: normalizeStoredValue(window.localStorage.getItem('UserPlan')),
  };
};

export const syncProductFruitsUser = () => {
  if (!isBrowser()) return;

  const userInfo = buildProductFruitsUser(readStoredProductFruitsUser());
  if (!userInfo) {
    if (hasInitialized || lastUserSignature) {
      destroyProductFruits();
    }
    return;
  }

  try {
    initOrUpdateProductFruits(userInfo);
  } catch (error) {
    console.error('Product Fruits sync failed', error);
  }
};

export const startProductFruitsObserver = () => {
  if (!isBrowser() || syncIntervalId !== null) return;

  syncProductFruitsUser();
  syncIntervalId = window.setInterval(syncProductFruitsUser, PRODUCT_FRUITS_SYNC_INTERVAL_MS);
};
